/**
 * Standard Actions configuration for Dawn.
 *
 * Keeps Dawn cart UI in sync with Shopify Standard Actions and also contains
 * The 3D Crystal storefront routing adjustments used by this draft theme.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/**
 * THE 3D CRYSTAL — Occasion page routing + Father's Day curation.
 */
function initT3dcOccasionRouting() {
  if (!window.location.pathname.replace(/\/+$/, '').endsWith('/pages/occasions')) return;
  const father = document.querySelector('[id^="collection-"][id*="featured_collection_Wex4P9"]');
  const approved = new Set(['wave-3d-crystal-for-fathers-day-gift','fathers-day-gift-heart-shaped-crystal-necklace','crystal-photo-keychain-with-led-light-fathers-gift','3d-crystal-photo-keychain-fathers-led-light-gift','3d-crystal-square-necklace-fathers-gift']);
  father?.querySelectorAll('li.grid__item').forEach(item => {
    const handle = item.querySelector('[data-product-handle]')?.dataset.productHandle;
    if (handle && !approved.has(handle)) item.remove();
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',initT3dcOccasionRouting,{once:true});
else initT3dcOccasionRouting();

/**
 * THE 3D CRYSTAL — Mobile first-tap builder routing.
 */
function initT3dcMobileSingleTapRouting() {
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  if (normalizedPath !== '/') return;
  if (!window.matchMedia('(max-width: 749px) and (pointer: coarse)').matches) return;

  const root = document.querySelector('.t3dc-home');
  if (!root) return;

  root.querySelectorAll('a[href*="view=create-your-crystal"]').forEach((link) => {
    if (link.dataset.t3dcSingleTap === 'true') return;
    link.dataset.t3dcSingleTap = 'true';
    link.style.touchAction = 'manipulation';

    let startX = 0;
    let startY = 0;
    let moved = false;
    let activeTouch = false;

    link.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) {
        activeTouch = false;
        return;
      }
      const touch = event.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      moved = false;
      activeTouch = true;
    }, { passive: true });

    link.addEventListener('touchmove', (event) => {
      if (!activeTouch || !event.touches[0]) return;
      const touch = event.touches[0];
      if (Math.abs(touch.clientX - startX) > 12 || Math.abs(touch.clientY - startY) > 12) {
        moved = true;
      }
    }, { passive: true });

    link.addEventListener('touchend', (event) => {
      if (!activeTouch || moved) {
        activeTouch = false;
        return;
      }
      activeTouch = false;
      const href = link.href;
      if (!href) return;
      event.preventDefault();
      event.stopPropagation();
      window.location.assign(href);
    }, { passive: false });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initT3dcMobileSingleTapRouting, { once: true });
} else {
  initT3dcMobileSingleTapRouting();
}

/**
 * THE 3D CRYSTAL — Desktop hero alignment + truly seamless background blend.
 * Only the photographic background is faded. The video remains fully opaque,
 * inset inside the neon frame on all four sides. Mobile is intentionally untouched.
 */
function initT3dcDesktopHeroBlendFix() {
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  if (normalizedPath !== '/') return;

  const old = document.getElementById('t3dc-desktop-hero-blend-fix');
  if (old) old.remove();

  const style = document.createElement('style');
  style.id = 't3dc-desktop-hero-blend-fix';
  style.textContent = `
@media screen and (min-width:990px){
  .t3dc-home{
    position:relative!important;
    background:linear-gradient(180deg,
      #0a568f 0%,
      #094b7d 16%,
      #083e67 31%,
      #073354 46%,
      #062944 60%,
      #051e32 74%,
      #03131f 88%,
      #02070c 100%)!important;
  }

  .t3dc-home .t3dc-home__hero{
    position:relative!important;
    isolation:isolate!important;
    overflow:hidden!important;
    background:none!important;
    background-image:none!important;
    -webkit-mask-image:none!important;
    mask-image:none!important;
    border:0!important;
    box-shadow:none!important;
  }

  .t3dc-home .t3dc-home__hero::before{
    content:""!important;
    display:block!important;
    position:absolute!important;
    inset:0!important;
    z-index:0!important;
    pointer-events:none!important;
    background-color:#071d31!important;
    background-image:url('https://cdn.shopify.com/s/files/1/0726/8152/1381/files/the-3d-crystal-home-hero-final-blended.png?v=1789071513')!important;
    background-repeat:no-repeat!important;
    background-position:center center!important;
    background-size:100% 100%!important;
    -webkit-mask-image:linear-gradient(to bottom,
      #000 0%,
      #000 73%,
      rgba(0,0,0,.98) 78%,
      rgba(0,0,0,.90) 83%,
      rgba(0,0,0,.72) 88%,
      rgba(0,0,0,.48) 93%,
      rgba(0,0,0,.20) 97%,
      transparent 100%)!important;
    mask-image:linear-gradient(to bottom,
      #000 0%,
      #000 73%,
      rgba(0,0,0,.98) 78%,
      rgba(0,0,0,.90) 83%,
      rgba(0,0,0,.72) 88%,
      rgba(0,0,0,.48) 93%,
      rgba(0,0,0,.20) 97%,
      transparent 100%)!important;
    -webkit-mask-size:100% 100%!important;
    mask-size:100% 100%!important;
    -webkit-mask-repeat:no-repeat!important;
    mask-repeat:no-repeat!important;
  }

  .t3dc-home .t3dc-home__hero::after{
    content:""!important;
    display:block!important;
    position:absolute!important;
    left:0!important;
    right:0!important;
    bottom:0!important;
    height:28%!important;
    z-index:1!important;
    pointer-events:none!important;
    background:linear-gradient(180deg,
      rgba(9,75,125,0) 0%,
      rgba(9,72,120,.08) 28%,
      rgba(8,66,109,.18) 52%,
      rgba(8,59,98,.28) 72%,
      rgba(7,53,84,.38) 88%,
      rgba(7,51,82,.46) 100%)!important;
  }

  /* Match the full decorative frame, then inset the actual video inside it. */
  .t3dc-home .t3dc-home__media{
    left:5.15%!important;
    top:16.55%!important;
    width:45.25%!important;
    height:50.05%!important;
    box-sizing:border-box!important;
    padding:11px 13px 12px!important;
    overflow:hidden!important;
    border-radius:24px!important;
    background:transparent!important;
    z-index:3!important;
  }

  .t3dc-home .t3dc-home__video-link{
    display:block!important;
    width:100%!important;
    height:100%!important;
    box-sizing:border-box!important;
    padding:0!important;
    overflow:hidden!important;
    border-radius:16px!important;
    background:#000!important;
    clip-path:inset(0 round 16px)!important;
  }

  .t3dc-home .t3dc-home__video{
    display:block!important;
    width:100%!important;
    height:100%!important;
    max-width:100%!important;
    max-height:100%!important;
    object-fit:cover!important;
    object-position:center center!important;
    border-radius:15px!important;
    margin:0!important;
    padding:0!important;
    transform:none!important;
  }

  .t3dc-home .t3dc-home__copy{z-index:4!important;}

  .t3dc-home .t3dc-real-reviews{
    position:relative!important;
    z-index:2!important;
    width:100vw!important;
    margin-left:calc(50% - 50vw)!important;
    margin-right:calc(50% - 50vw)!important;
    margin-top:-1px!important;
    padding-top:74px!important;
    background:transparent!important;
    border-top:0!important;
    box-shadow:none!important;
    overflow:visible!important;
  }

  .t3dc-home .t3dc-real-reviews::before{
    content:none!important;
    display:none!important;
    background:none!important;
  }

  .t3dc-home .t3dc-real-reviews__inner{
    position:relative!important;
    z-index:2!important;
  }
}
`;
  document.head.appendChild(style);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initT3dcDesktopHeroBlendFix, { once: true });
} else {
  initT3dcDesktopHeroBlendFix();
}
